import { getHazardList } from "@/features/branding";
import { HacsOmUploadData } from "@/features/hacs";
import { anaHazards } from "@/lib/crg_apis/constants/hazards";
import { AnaHazardId } from "@/store/services/facades/types";
import { useGetCurrentGroupIdQuery, useGetGroupsQuery, useGetToolUsageQuery } from "@/store/services/supabase";
import { formatMetresSquaredAsKmSquared } from "@/tools/create/lss-from-map/utils";
import { useIsOwner } from "@/utils/hooks/useIsOwner";
import { useKeys } from "@/utils/hooks/useKeys";
import { removeFileExtension } from "@/utils/string";
import { ArrowUpOutlined } from "@ant-design/icons";
import * as turf from "@turf/turf";
import { Alert, Button, Divider, Input, Progress, Select, Statistic, Switch, Upload } from "antd";
import classnames from "classnames";
import type { Polygon } from "leaflet";
import Link from "next/link";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Markdown, { Components } from "react-markdown";
import s from "../../../aggregate/portfolio-export/components/PortfolioExportUploadReview.module.scss";
export interface LargeSiteScreenUploadReviewProps {
  /** Handles whenever a CSV is selected. */
  setData: (data?: HacsOmUploadData) => void;
  /** File data for the chosen CSV upload. */
  data: HacsOmUploadData;
  onContinue: () => void;
  polygons: ReturnType<Polygon["toGeoJSON"]>[];
  setRiskiestPoint: (data: {
    hazard: AnaHazardId | "total";
    year: number;
  }) => void;
}
export const LargeSiteScreenUploadReview = ({
  data,
  setData,
  setRiskiestPoint,
  onContinue,
  polygons
}: LargeSiteScreenUploadReviewProps) => {
  const currentUsage = useGetToolUsageQuery({
    activeQuotaOnly: true
  }).data?.largeSite;
  const {
    data: groups
  } = useGetGroupsQuery();
  const {
    data: currentGroupId
  } = useGetCurrentGroupIdQuery();
  const isOwner = useIsOwner();
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "screening.largeSite.uploadLimitAlert"
  });
  const [filename, setFilename] = useState(removeFileExtension(data.file.name || ""));
  const [isPrivate, setIsPrivate] = useState(true);
  const [disablePrivateToggle, setDisablePrivateToggle] = useState(true);
  const [keys] = useKeys();
  const [groupId, setGroupId] = useState("");
  const [totalPolygonArea, setTotalPolygonArea] = useState(0);
  const [year, setYear] = useState<number>(2050);
  const [hazard, setHazard] = useState<AnaHazardId | "total">("total");
  useEffect(() => {
    if (currentGroupId?.id) {
      setGroupId(currentGroupId.id);
    }
  }, [currentGroupId]);
  useEffect(() => {
    if (keys.orgSecret) {
      setIsPrivate(false);
      setDisablePrivateToggle(false);
    }
  }, [keys]);
  useEffect(() => {
    try {
      setTotalPolygonArea(polygons.reduce<number>((total, polygon) => {
        return total + turf.area(polygon);
      }, 0));
    } catch {
      setTotalPolygonArea(0);
    }
  }, [polygons]);
  if (!currentUsage) return <></>;
  const quota = currentUsage.quota;
  const usage = currentUsage.usage;

  // Don't count the header row.
  const uploadedAssetCount = data.lineCount - 1;
  const uploadedAssetPercent = 1 / quota * 100;
  const futureUsage = usage + 1;
  const futureUsagePercent = futureUsage / quota * 100;
  const quotaUsedPercent = 1 - (quota - usage) / quota;
  const quotaRemaining = quota - usage;
  const showWarning = quotaRemaining === 1;
  const showError = futureUsagePercent > 100;
  const exceedsQuotaBuffer = parseFloat(futureUsagePercent.toFixed(2)) > currentUsage.hardLimitPercent;
  const handleContinue = () => {
    setData({
      ...data,
      name: filename,
      groupId: isPrivate && currentGroupId ? currentGroupId.id : groupId,
      isPrivate
    });
    setRiskiestPoint({
      hazard,
      year
    });
    onContinue();
  };
  const groupOpts = groups && groups.map(g => ({
    key: g.id,
    value: g.id,
    label: g.name || ""
  }));
  const StatsRowContainer = ({
    title,
    children
  }: {
    title: string;
    children: ReactNode;
  }) => {
    return <div className={s.statsRowContainer} data-sentry-component="StatsRowContainer" data-sentry-source-file="LargeSiteScreenUploadReview.tsx">
        <div className={s.statsTitle}>{title}</div>
        {children}
      </div>;
  };
  const components: Components = {
    a: props => {
      return <Link href={props.href || ""} target="_blank">
          {props.children}
        </Link>;
    }
  };
  return <div className={s.fileUploadContainer} data-sentry-component="LargeSiteScreenUploadReview" data-sentry-source-file="LargeSiteScreenUploadReview.tsx">
      <div className={s.fileContainer}>
        <Upload listType="text" disabled defaultFileList={[{
        uid: "0",
        status: "done",
        name: data.file.name
      }]} data-sentry-element="Upload" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
      </div>

      <fieldset className={s.fileContainer}>
        <legend>Review Upload</legend>
        <div className={s.statsContainer}>
          <div className={s.statsRowContainer}>
            <div className={s.statsTitle}>Name</div>
            <Input className={s.fileInfoName} value={filename} onChange={val => setFilename(val.target.value)} data-sentry-element="Input" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
          </div>

          <div className={s.statsRowContainer}>
            <div className={s.statsTitle}>Private</div>
            <div className="!col-span-2">
              <Switch value={isPrivate} disabled={disablePrivateToggle} onChange={val => setIsPrivate(val)} data-sentry-element="Switch" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
            </div>
          </div>

          <div className={classnames(s.statsRowContainer, "mb-3 !h-3")}>
            <div className="col-span-2 col-start-2 h-4 text-xs text-gray-500">
              {isPrivate ? "Your large site will be only visible to you." : "Your large site will be visible to everyone in the group."}
            </div>
          </div>

          {isOwner && !isPrivate && keys.orgSecret && <div className={s.statsRowContainer}>
              <div className={s.statsTitle}>Share with</div>
              <Select className={s.fileInfoName} placeholder="Select group..." options={groupOpts} onChange={e => setGroupId(e)} value={groupId} />
            </div>}

          <div className={s.statsRowContainer}>
            <div className={s.statsTitle}>Total Polygon Area</div>
            <Statistic value={formatMetresSquaredAsKmSquared(totalPolygonArea)} className={s.statsValue} data-sentry-element="Statistic" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
          </div>

          <div className={s.statsRowContainer} style={{
          marginBottom: "2em"
        }}>
            <div className={s.statsTitle}>Total Grid Points</div>
            <Statistic value={uploadedAssetCount} className={s.statsValue} data-sentry-element="Statistic" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
          </div>

          <Divider plain orientation="left" style={{
          margin: 0,
          marginBottom: "0.5em",
          color: "var(--color-gray-400)"
        }} orientationMargin={"0"} data-sentry-element="Divider" data-sentry-source-file="LargeSiteScreenUploadReview.tsx">
            Riskiest Grid Point Settings
          </Divider>

          <Alert type="info" className="mb-2 mt-1" message="Along with a summary of the risk profile of the entire large site, this tool will return detailed results for the single grid point with the highest MVAR% based on your chosen criteria. Select the year and hazard(s) you want to use to define the 'riskiest grid point' for this analysis." data-sentry-element="Alert" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />

          <StatsRowContainer title="Year of Interest" data-sentry-element="StatsRowContainer" data-sentry-source-file="LargeSiteScreenUploadReview.tsx">
            <Select className="!col-span-2 w-full" options={[{
            value: 2025
          }, {
            value: 2050
          }, {
            value: 2100
          }]} value={year} onChange={setYear} data-sentry-element="Select" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
          </StatsRowContainer>

          <StatsRowContainer title="Hazard of Interest" data-sentry-element="StatsRowContainer" data-sentry-source-file="LargeSiteScreenUploadReview.tsx">
            <Select className="!col-span-2 w-full" options={[{
            label: "All Hazards (Total)",
            value: "total"
          }, ...getHazardList().available.filter(hazard => hazard !== "heat") // Extreme Heat does not have damage values.
          .map(value => {
            const hazard = anaHazards[value];
            return {
              label: hazard.name,
              value
            };
          }).sort((a, b) => a.label > b.label ? 1 : -1)]} value={hazard} onChange={setHazard} data-sentry-element="Select" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
          </StatsRowContainer>

          <Divider plain orientation="left" style={{
          margin: 0,
          marginTop: "2rem",
          marginBottom: "0.5em",
          color: "var(--color-gray-400)"
        }} orientationMargin={"0"} data-sentry-element="Divider" data-sentry-source-file="LargeSiteScreenUploadReview.tsx">
            Usage After Upload
          </Divider>

          <div className={s.statsRowContainer}>
            <div className={s.statsTitle}>Reports Created</div>
            <Statistic value={futureUsage} className={`${s.statsValue} ${showWarning && s.warning} ${showError && s.error}`} suffix={`/ ${quota?.toLocaleString() ?? "<Limit Not Set>"}`} data-sentry-element="Statistic" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
            <Statistic value={1} valueStyle={{
            color: "var(--color-gray-400)"
          }} prefix={<ArrowUpOutlined />} className={s.statsValue} data-sentry-element="Statistic" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
          </div>

          <div className={s.statsRowContainer}>
            <div className={s.statsTitle}>Reports Created %</div>
            <Statistic value={futureUsagePercent} precision={2} suffix="%" className={`${s.statsValue} ${showWarning && s.warning} ${showError && s.error}`} data-sentry-element="Statistic" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
            <Statistic value={uploadedAssetPercent} precision={2} valueStyle={{
            color: "var(--color-gray-400)"
          }} prefix={<ArrowUpOutlined />} suffix="%" className={s.statsValue} data-sentry-element="Statistic" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
          </div>

          <div className={s.statsRowContainer}>
            <Progress percent={futureUsagePercent} success={{
            percent: quotaUsedPercent,
            strokeColor: "#4B1553"
          }} className={`${s.progressContainer} ${showError && s.error}`} showInfo={false} style={{
            gridColumn: "2 / 4"
          }} data-sentry-element="Progress" data-sentry-source-file="LargeSiteScreenUploadReview.tsx" />
          </div>
        </div>
      </fieldset>

      {showWarning && <Alert message={<Markdown components={components}>{isOwner ? t("owner.warning") : t("user.warning")}</Markdown>} type="warning" showIcon className={s.alert} />}

      {showError && <Alert message={<div>
              <div>
                <strong>Limit Reached</strong>
              </div>
              {<Markdown components={components}>{isOwner ? t("owner.error") : t("user.error")}</Markdown>}
            </div>} type="error" showIcon className={s.alert} />}

      <div className={s.actionButtons}>
        <Button type="primary" onClick={handleContinue} disabled={currentUsage.alwaysEnabled ? false : currentUsage.enabled ? exceedsQuotaBuffer : true} data-sentry-element="Button" data-sentry-source-file="LargeSiteScreenUploadReview.tsx">
          Run Analysis
        </Button>
      </div>
    </div>;
};